/**
 * Search-flyout GA4 instrumentation.
 *
 * Publishes `ocs:search_flyout` and `ocs:null_search` through
 * window.OCSAnalyticsEvents (assets/ocs-analytics-events.js), the sole owner of the Shopify analytics
 * publish call — see the ARCH-001 guard in tests/analytics. The Custom Pixel
 * owns the dataLayer mapping: nothing here touches dataLayer, and nothing here
 * may, because GTM runs inside the Web Pixel sandbox and the storefront has no
 * dataLayer at all.
 *
 * This replaces the production GTM Custom HTML tag `HTML - Click listeners -
 * SEARCH FLYOUT` (tagId 337), which fired on DOM_READY, cached five container
 * elements with document.querySelector, and bound delegated click listeners to
 * them. Sandboxed GTM cannot see the storefront DOM, so that tag is dead here.
 *
 * Two things deliberately diverge from that tag's implementation:
 *
 *   1. Product fields come from the Algolia hit, not from scraped markup.
 *      Production read `item.getElementsByClassName('search-result__vendor')[0]
 *      .innerText`, which throws — and silently drops the whole event — on any
 *      result missing the class. Horizon has the hit in scope at render time.
 *
 *   2. Listeners are delegated on `document`, not bound to cached containers.
 *      Algolia destroys and rebuilds the panel on every keystroke, so a cached
 *      container reference is stale within one typed character.
 *
 * Every payload carries all eleven keys, empty where they do not apply. GTM
 * dataLayer variables persist between pushes, so omitting `vendor` on a
 * collection click leaves {{DLV - vendor}} resolving to the previous product's
 * vendor. Explicit empties are what stop that bleed.
 *
 * Null searches are a separate `null_search` event rather than a search-flyout
 * section. They report only after the search results page confirms zero results.
 */
(function () {
  'use strict';

  var FLYOUT_EVENT_NAME = 'ocs:search_flyout';
  var SEARCH_EVENT_NAME = 'ocs:search';
  var NULL_SEARCH_EVENT_NAME = 'ocs:null_search';

  /**
   * `section` values are analytics keys, lifted verbatim from the production
   * tag. They are never localized — GA4 reports fragment across languages if
   * they are. `title` and `section_heading` are human-readable and production
   * read them as rendered innerText, so those stay localized.
   */
  var SECTION_PRODUCTS = 'SEARCH FLYOUT - PRODUCTS';
  var SECTION_COLLECTIONS = 'SEARCH FLYOUT - COLLECTIONS';
  var SECTION_PAGES = 'SEARCH FLYOUT - PAGES';
  var SECTION_ARTICLES = 'SEARCH FLYOUT - ARTICLES';
  var SECTION_VIEW_ALL = 'SEARCH FLYOUT - VIEW ALL';


  /** Algolia source id -> `section`. Products are excluded: they have no
   *  data-autocomplete-source-id container of their own and are published from
   *  the template hook instead. */
  var SOURCE_SECTIONS = {
    collections: SECTION_COLLECTIONS,
    pages: SECTION_PAGES,
    articles: SECTION_ARTICLES,
  };

  var QUERY_INPUT_SELECTOR = '#algolia-search-bar .aa-Input';
  var DETACHED_QUERY_INPUT_SELECTOR = '.aa-DetachedContainer .aa-Input';
  var PRODUCTS_PANEL_SELECTOR = '.aa-PanelSection--right';
  var SOURCE_HEADING_SELECTOR = '.aa-SourceHeaderTitle';
  var FOOTER_SELECTOR = '.aa-SourceFooter';

  /**
   * Availability vocabulary. Horizon already has a controlled set for this
   * concept — see validAvailability in assets/ocs-analytics-events.js:499 —
   * and every other OCS event reports item availability with it.
   *
   * Production sent the *rendered* inventory text instead ('Available In-Store'
   * / 'Out of Stock Online', absent when purchasable), which was an artifact of
   * scraping innerText: the values were localized, so French sessions reported
   * different strings for the same state. Using the theme vocabulary here keeps
   * `inventory_status` joinable with `item_availability` on every other event.
   */
  var AVAILABILITY_IN_STOCK = 'In-Stock';
  var AVAILABILITY_IN_STORE_ONLY = 'In-Store Only';
  var AVAILABILITY_OUT_OF_STOCK = 'Out of Stock';

  function publish(name, payload) {
    var api = window.OCSAnalyticsEvents;
    if (!api || typeof api.publishOcsAnalyticsEvent !== 'function') return false;
    return api.publishOcsAnalyticsEvent(name, payload);
  }

  /* ------------------------------------------------------------------ *
   * Pure helpers — no DOM, no window. Everything testable lives here.
   * ------------------------------------------------------------------ */

  /**
   * Mirrors isInStoreOnly in assets/algolia-autocomplete.js:142.
   * @param {object} hit
   * @returns {boolean}
   */
  function isInStoreOnly(hit) {
    var meta = hit && hit.meta;
    var variant = meta && meta.variant;
    return !!variant && variant.availability === 'in-store';
  }

  /**
   * Mirrors isNewProduct in assets/algolia-autocomplete.js:146, including its
   * rule that an in-store-only product is never flagged new.
   * @param {object} hit
   * @returns {boolean}
   */
  function isNewProduct(hit) {
    if (!hit || isInStoreOnly(hit)) return false;
    var tags = hit.tags || hit._tags || [];
    var i;
    if (!Array.isArray(tags)) return false;
    for (i = 0; i < tags.length; i += 1) {
      if (typeof tags[i] === 'string' && tags[i].toLowerCase() === 'status--new') return true;
    }
    return false;
  }

  /**
   * Mirrors hasPriceDrop in assets/algolia-autocomplete.js:152.
   * @param {object} hit
   * @returns {boolean}
   */
  function hasPriceDrop(hit) {
    if (!hit) return false;
    return !!(
      hit.compare_at_price &&
      hit.compare_at_price > 0 &&
      hit.compare_at_price > hit.price
    );
  }

  /**
   * Mirrors isAvailableForOrder in assets/algolia-plp-hooks.js:363 — the
   * established read for "purchasable online" on an Algolia hit. The field
   * varies by index generation, hence the four-way check.
   * @param {object} hit
   * @returns {boolean}
   */
  function isAvailableForOrder(hit) {
    if (!hit) return false;
    return (
      hit.can_order === true ||
      hit.inventory_available === true ||
      hit.available === true ||
      hit.inventory_quantity > 0
    );
  }

  /**
   * @param {object} hit
   * @returns {string} one of the three AVAILABILITY_* values.
   */
  function availabilityForHit(hit) {
    if (isInStoreOnly(hit)) return AVAILABILITY_IN_STORE_ONLY;
    if (isAvailableForOrder(hit)) return AVAILABILITY_IN_STOCK;
    return AVAILABILITY_OUT_OF_STOCK;
  }

  /**
   * Fills the eleven-key contract, so a caller only supplies what applies to
   * its section and every other key still ships as an explicit empty. This is
   * the single guard against GTM dataLayer-variable staleness; see the file
   * header.
   *
   * @param {object} partial
   * @returns {object} always the same eleven keys, in a stable order.
   */
  function buildPayload(partial) {
    var given = partial || {};
    function take(key) {
      var value = given[key];
      if (value === undefined || value === null) return '';
      return value;
    }
    return {
      section: take('section'),
      section_heading: take('section_heading'),
      title: take('title'),
      index: take('index'),
      vendor: take('vendor'),
      price_drop: take('price_drop'),
      new_item: take('new_item'),
      inventory_status: take('inventory_status'),
      query: take('query'),
      age_verified: take('age_verified'),
      locale: take('locale'),
    };
  }

  /**
   * @param {object} hit the full Algolia hit.
   * @param {object} context {title, vendor, sectionHeading, index, query,
   *   ageVerified, locale} — title and vendor arrive already localized from the
   *   template hook, which resolves the fr-CA translation fields.
   * @returns {object}
   */
  function buildProductPayload(hit, context) {
    var ctx = context || {};
    return buildPayload({
      section: SECTION_PRODUCTS,
      section_heading: ctx.sectionHeading,
      title: ctx.title,
      index: ctx.index,
      vendor: ctx.vendor,
      price_drop: hasPriceDrop(hit),
      new_item: isNewProduct(hit),
      inventory_status: availabilityForHit(hit),
      query: ctx.query,
      age_verified: ctx.ageVerified,
      locale: ctx.locale,
    });
  }

  /**
   * Collections, pages and articles. Production sent exactly section,
   * section_heading, title and index for these.
   *
   * @param {string} section one of the SECTION_* constants.
   * @param {object} context {title, sectionHeading, index, query, ageVerified,
   *   locale}
   * @returns {object}
   */
  function buildLinkPayload(section, context) {
    var ctx = context || {};
    return buildPayload({
      section: section,
      section_heading: ctx.sectionHeading,
      title: ctx.title,
      index: ctx.index,
      query: ctx.query,
      age_verified: ctx.ageVerified,
      locale: ctx.locale,
    });
  }

  /**
   * "Show All Results". Production sent only section and title — no heading and
   * no index, because the footer sits outside every source list.
   *
   * @param {object} context {title, query, ageVerified, locale}
   * @returns {object}
   */
  function buildViewAllPayload(context) {
    var ctx = context || {};
    return buildPayload({
      section: SECTION_VIEW_ALL,
      title: ctx.title,
      query: ctx.query,
      age_verified: ctx.ageVerified,
      locale: ctx.locale,
    });
  }

  /**
   * A null search has its own event contract and carries only the query that
   * returned no results.
   *
   * @param {object} context {query}
   * @returns {object}
   */
  function buildNullSearchPayload(context) {
    var ctx = context || {};
    return { query: typeof ctx.query === 'string' ? ctx.query : '' };
  }

  /* ------------------------------------------------------------------ *
   * DOM readers.
   * ------------------------------------------------------------------ */

  /** @returns {string} the live query, same expression as
   *  assets/algolia-autocomplete.js:410. */
  function readQuery(doc) {
    var scope = doc || document;
    var input = scope.querySelector(QUERY_INPUT_SELECTOR) || scope.querySelector(DETACHED_QUERY_INPUT_SELECTOR);
    var value = input && input.value;
    return typeof value === 'string' ? value.trim() : '';
  }

  /**
   * The same `age_verified` cookie the age gate writes — see
   * assets/ocs-age-gate.js:3. Read here rather than in GTM because the
   * production source, the `CJS - Age Verified Status` variable, calls
   * Cookies.get() inside the sandbox, where storefront cookies are not visible.
   *
   * Note this is *not* isAgeGateRestrictedSearch() in algolia-autocomplete.js:60,
   * which reports whether results are being filtered for the restricted
   * experience — a different concept.
   *
   * @returns {boolean}
   */
  function readAgeVerified(doc) {
    var raw = (doc || document).cookie;
    if (typeof raw !== 'string' || raw === '') return false;
    var parts = raw.split(';');
    var i;
    var pair;
    var name;
    for (i = 0; i < parts.length; i += 1) {
      pair = parts[i];
      name = pair.slice(0, pair.indexOf('=')).trim();
      if (name !== 'age_verified') continue;
      return pair.slice(pair.indexOf('=') + 1).trim() === 'verified';
    }
    return false;
  }

  /** @returns {string} 'en' or 'fr', matching the convention in
   *  assets/ocs-pdp-analytics.js:115. */
  function readLocale(doc) {
    var root = (doc || document).documentElement;
    var lang = root && root.lang;
    if (lang) return lang;
    var shopify = window.Shopify;
    return (shopify && shopify.locale) || '';
  }

  /**
   * The localized source heading, already rewritten by the observer at
   * assets/algolia-autocomplete.js:462.
   *
   * @param {Element} container the source container, or the products panel.
   * @returns {string}
   */
  function readSectionHeading(container) {
    if (!container || typeof container.querySelector !== 'function') return '';
    var heading = container.querySelector(SOURCE_HEADING_SELECTOR);
    var text = heading && heading.textContent;
    return typeof text === 'string' ? text.trim() : '';
  }

  /**
   * 1-based rank of an item within its list, counting rendered siblings.
   *
   * Algolia's item template receives no index, and product cards carry no
   * position attribute, so position has to come from the DOM. Counting
   * `li` siblings is stable under the mobile reflow at
   * assets/algolia-autocomplete.js:512, which moves the whole products panel
   * without reordering anything inside it.
   *
   * @param {Element} element any node inside the item.
   * @returns {number|string} 1-based rank, or '' when there is no list.
   */
  function rankWithinList(element) {
    if (!element || typeof element.closest !== 'function') return '';
    var item = element.closest('li');
    if (!item || !item.parentNode) return '';
    var siblings = item.parentNode.children;
    var rank = 0;
    var i;
    for (i = 0; i < siblings.length; i += 1) {
      if (siblings[i].tagName === 'LI') rank += 1;
      if (siblings[i] === item) return rank;
    }
    return '';
  }

  /** Shared per-click context, read once so all five paths agree. */
  function ambientContext(doc) {
    return {
      query: readQuery(doc),
      ageVerified: readAgeVerified(doc),
      locale: readLocale(doc),
    };
  }

  function elementText(element) {
    var text = element && (element.innerText || element.textContent);
    return typeof text === 'string' ? text.trim() : '';
  }

  /* ------------------------------------------------------------------ *
   * Publish paths.
   * ------------------------------------------------------------------ */

  /**
   * Called from the beforeAutocompleteProductTemplate hook in
   * assets/algolia-autocomplete.js, which already holds the hit and the
   * locale-resolved title and vendor.
   *
   * Wrapped so a failure here can never break navigation: the hook calls this
   * from an onClick that also runs trackSearchAttribution.
   *
   * @param {object} hit the full Algolia hit.
   * @param {Element} anchor the clicked .aa-ItemLink.
   * @param {object} [strings] {title, vendor} already localized.
   * @returns {boolean} whether the event was published.
   */
  function publishFlyoutProduct(hit, anchor, strings) {
    try {
      var doc = (anchor && anchor.ownerDocument) || document;
      var text = strings || {};
      var panel = anchor && typeof anchor.closest === 'function'
        ? anchor.closest(PRODUCTS_PANEL_SELECTOR)
        : null;
      var context = ambientContext(doc);
      context.sectionHeading = readSectionHeading(panel);
      context.index = rankWithinList(anchor);
      context.title = text.title || (hit && hit.title) || '';
      context.vendor = text.vendor || (hit && hit.vendor) || '';
      return publish(FLYOUT_EVENT_NAME, buildProductPayload(hit, context));
    } catch (error) {
      return false;
    }
  }

  /**
   * Collections, pages, articles and the footer. One listener on `document`,
   * because the panel is rebuilt on every keystroke.
   *
   * @param {Event} event
   */
  function handleDocumentClick(event) {
    var target = event && event.target;
    if (!target || typeof target.closest !== 'function') return;

    var doc = target.ownerDocument || document;
    var context;

    var footer = target.closest(FOOTER_SELECTOR);
    if (footer) {
      var trigger = target.closest('a, button') || footer;
      context = ambientContext(doc);
      context.title = elementText(trigger);
      publish(FLYOUT_EVENT_NAME, buildViewAllPayload(context));
      return;
    }

    var link = target.closest('a');
    if (!link) return;

    var source = link.closest('[data-autocomplete-source-id]');
    if (!source) return;
    var sourceId = (source.getAttribute('data-autocomplete-source-id') || '').toLowerCase();
    var section = SOURCE_SECTIONS[sourceId];
    if (!section) return;

    context = ambientContext(doc);
    context.sectionHeading = readSectionHeading(source);
    context.title = elementText(link);
    context.index = rankWithinList(link);
    publish(FLYOUT_EVENT_NAME, buildLinkPayload(section, context));
  }

  /**
   * Queries whose first complete search-page result state has been observed.
   * A later filter change that reduces a previously successful query to zero
   * must not turn that original search into a null search.
   */
  var resolvedSearchPageQueries = [];

  /**
   * Called by the search results integration only after both product and
   * secondary Algolia result counts are known. This means typing a no-results
   * query in the flyout is silent; the event can fire only after navigation to
   * the search page.
   *
   * @param {string} query
   * @param {boolean} hasResults
   * @returns {boolean} whether a null_search event was published.
   */
  function reportSearchPageResult(query, hasResults) {
    var pathname = window.location && window.location.pathname;
    var term = typeof query === 'string' ? query.trim() : '';
    if (typeof pathname !== 'string' || !/\/search\/?$/.test(pathname)) return false;
    if (term === '') return false;
    if (resolvedSearchPageQueries.indexOf(term) !== -1) return false;

    resolvedSearchPageQueries.push(term);
    if (hasResults) return publish(SEARCH_EVENT_NAME, { query: term });
    return publish(NULL_SEARCH_EVENT_NAME, buildNullSearchPayload({ query: term }));
  }

  function init(root) {
    if (!root || typeof root.addEventListener !== 'function') return;
    root.addEventListener('click', function onFlyoutClick(event) {
      try {
        handleDocumentClick(event);
      } catch (error) {
        /* Analytics must never interrupt a navigation. */
      }
    });
  }

  window.OCSSearchAnalytics = {
    availabilityForHit: availabilityForHit,
    buildLinkPayload: buildLinkPayload,
    buildNullSearchPayload: buildNullSearchPayload,
    buildPayload: buildPayload,
    buildProductPayload: buildProductPayload,
    buildViewAllPayload: buildViewAllPayload,
    handleDocumentClick: handleDocumentClick,
    hasPriceDrop: hasPriceDrop,
    init: init,
    isNewProduct: isNewProduct,
    publishFlyoutProduct: publishFlyoutProduct,
    rankWithinList: rankWithinList,
    reportSearchPageResult: reportSearchPageResult,
    readAgeVerified: readAgeVerified,
    readLocale: readLocale,
    readQuery: readQuery,
    readSectionHeading: readSectionHeading,
    SECTIONS: {
      products: SECTION_PRODUCTS,
      collections: SECTION_COLLECTIONS,
      pages: SECTION_PAGES,
      articles: SECTION_ARTICLES,
      viewAll: SECTION_VIEW_ALL,
    },
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function bootstrap() {
      init(document);
    });
  } else {
    init(document);
  }
})();
